import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Home, QrCode, Clock, Bell, LogOut } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_baduwork-tracker/artifacts/8tdmywqb_Badu%20Facility%20Services.png";

const navItems = [
  { icon: Home, label: "Início", path: "/worker" },
  { icon: QrCode, label: "Escanear", path: "/worker/scan" },
  { icon: Clock, label: "Histórico", path: "/worker/history" },
  { icon: Bell, label: "Alertas", path: "/worker/notifications" },
];

export default function WorkerLayout({ children, hideNav = false }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (!hideNav) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [hideNav]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (hideNav) {
    return <div className="min-h-screen bg-gray-50">{children}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Gradient bar at top */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2ECC40] to-[#F5A623] z-50"></div>

      {/* Header */}
      <header className="sticky top-1 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="Badu Facility Services" 
              className="h-8"
              data-testid="worker-header-logo"
            />
            <span className="font-bold gradient-text text-sm">PONTO FACIL</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#2ECC40] to-[#F5A623] flex items-center justify-center text-white text-sm font-bold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-gray-400 hover:text-red-500"
              data-testid="worker-logout-btn"
            >
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <div className="flex justify-around items-center">
          {navItems.map(({ icon: Icon, label, path }) => {
            const isActive = location.pathname === path;
            const showBadge = path === "/worker/notifications" && unreadCount > 0;
            
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`flex flex-col items-center gap-1 py-2 px-4 rounded-lg transition-colors relative ${
                  isActive 
                    ? "text-[#F5A623]" 
                    : "text-gray-400 hover:text-gray-600"
                }`}
                data-testid={`worker-nav-${path.split("/").pop() || "home"}`}
              >
                <div className="relative">
                  <Icon size={24} />
                  {showBadge && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-[#F5A623] to-[#e8940a] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </div>
                <span className="text-xs font-medium">{label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
